import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

export interface ConfirmDialogData {
  title: string;
  message: string;
  /** Optional consequence bullet points displayed under the message. */
  consequences?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
  /** When set, the user must type this exact value to enable the confirm button. */
  requireNameMatch?: string;
}

@Component({
  selector: 'app-confirm-dialog',
  imports: [MatDialogModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6 w-full">
      <!-- Header -->
      <div class="flex items-start gap-3 mb-4">
        <div class="rounded-full bg-red-50 dark:bg-red-900/30 p-2 shrink-0">
          <mat-icon class="!text-xl text-red-600 dark:text-red-400">warning</mat-icon>
        </div>
        <div class="min-w-0">
          <h2 data-testid="confirm-dialog-heading" class="text-lg font-semibold text-zinc-900 dark:text-white leading-tight">{{ data.title }}</h2>
          <p data-testid="confirm-dialog-message" class="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{{ data.message }}</p>
        </div>
      </div>

      <!-- Consequences -->
      @if (data.consequences?.length) {
        <ul class="mb-4 ml-1 space-y-1.5 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5">
          @for (item of data.consequences; track item) {
            <li class="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-200">
              <mat-icon class="!text-sm !w-4 !h-4 !leading-4 shrink-0">arrow_right</mat-icon>
              <span>{{ item }}</span>
            </li>
          }
        </ul>
      }

      <!-- Type-to-confirm -->
      @if (data.requireNameMatch) {
        <div class="mb-4 space-y-1.5">
          <label for="confirm-name-input" class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Type <span class="font-mono font-semibold text-zinc-900 dark:text-zinc-100">{{ data.requireNameMatch }}</span> to confirm
          </label>
          <input
            id="confirm-name-input"
            data-testid="confirm-dialog-name-input"
            type="text"
            autocomplete="off"
            [value]="typedName()"
            (input)="onTypedNameChange($event)"
            class="flex h-9 w-full rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-1.5 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            [placeholder]="data.requireNameMatch"
          >
        </div>
      }

      <!-- Actions -->
      <div class="flex justify-end gap-3 pt-2">
        <button
          data-testid="confirm-dialog-cancel-btn"
          type="button"
          class="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors cursor-pointer"
          (click)="dialogRef.close(false)"
        >
          {{ data.cancelLabel || 'Cancel' }}
        </button>
        <button
          data-testid="confirm-dialog-confirm-btn"
          type="button"
          class="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium bg-red-600 text-white hover:bg-red-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          [disabled]="!canConfirm()"
          (click)="dialogRef.close(true)"
        >
          {{ data.confirmLabel || 'Delete' }}
        </button>
      </div>
    </div>
  `
})
export class ConfirmDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ConfirmDialogComponent>);
  readonly data = inject<ConfirmDialogData>(MAT_DIALOG_DATA);

  typedName = signal('');

  canConfirm = computed(() =>
    !this.data.requireNameMatch || this.typedName().trim() === this.data.requireNameMatch
  );

  onTypedNameChange(event: Event): void {
    this.typedName.set((event.target as HTMLInputElement).value);
  }
}
